import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface RepairProgressDialogData {
  scannedDate: string;
  idCount: number;
}

/**
 * Shown for as long as a repair is running.
 *
 * Modal on purpose: the run writes transaction ids, then statuses, then NSF imports into NLS,
 * and none of that should be competing with an operator starting a second scan or navigating
 * away mid-write.
 *
 * The steps are listed but not ticked off, and that is deliberate rather than unfinished. The
 * repair is one request — it has to be, or a status step could run without the id step that
 * must precede it — so the browser learns nothing until it returns. Inventing per-step
 * progress would mean animating a guess, and a guess that said "posting NSF to NLS" while the
 * server was still reading ids would be worse than saying nothing. The elapsed clock is the
 * one honest signal available, so that is what is shown moving.
 */
@Component({
  selector: 'app-repair-progress-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './repair-progress-dialog.component.html',
  styleUrls: ['./repair-progress-dialog.component.scss'],
})
export class RepairProgressDialogComponent implements OnInit, OnDestroy {
  elapsedSeconds = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(@Inject(MAT_DIALOG_DATA) public readonly data: RepairProgressDialogData) {}

  ngOnInit(): void {
    this.timer = setInterval(() => (this.elapsedSeconds += 1), 1000);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  get elapsedLabel(): string {
    if (this.elapsedSeconds < 60) return `${this.elapsedSeconds}s`;
    const minutes = Math.floor(this.elapsedSeconds / 60);
    return `${minutes}m ${String(this.elapsedSeconds % 60).padStart(2, '0')}s`;
  }
}
